import {FEATURE_HIGHLIGHTS} from './features'

const PENDING_VISIT_SUFFIX = ':pending-visit'
const STORAGE_PROBE_KEY = '__feature_highlight_storage_probe__'
/** Shared marker in FeatureHighlight storage keys (local + session). */
const FEATURE_HIGHLIGHT_KEY_MARKER = 'feature-highlight'

/**
 * Partition highlight storage by account + tenant so one admin's dismiss
 * does not hide the prompt for another identity in the same browser.
 */
export const getFeatureHighlightStorageKey = (
  storageKey: string,
  emailId?: string | null,
  primaryDomain?: string | null,
): string | null => {
  const account = emailId?.trim().toLowerCase() ?? ''
  const tenant = primaryDomain?.trim().toLowerCase() ?? ''
  if (!storageKey || !account || !tenant) return null
  return `${storageKey}:${account}:${tenant}`
}

const isActiveFeatureHighlightStorageKey = (key: string): boolean =>
  FEATURE_HIGHLIGHTS.some(feature => key.startsWith(`${feature.storageKey}:`))

const pendingVisitKey = (storageKey: string): string =>
  `${storageKey}${PENDING_VISIT_SUFFIX}`

const canUseStorage = (storage: Storage): boolean => {
  try {
    storage.setItem(STORAGE_PROBE_KEY, '1')
    storage.removeItem(STORAGE_PROBE_KEY)
    return true
  } catch {
    return false
  }
}

/** True when both localStorage and sessionStorage support read/write. */
export const isFeatureHighlightStorageAvailable = (): boolean => {
  try {
    return (
      typeof window !== 'undefined' &&
      canUseStorage(window.localStorage) &&
      canUseStorage(window.sessionStorage)
    )
  } catch {
    return false
  }
}

export const hasSeenFeatureHighlight = (storageKey: string): boolean => {
  try {
    return window.localStorage.getItem(storageKey) === 'true'
  } catch {
    return false
  }
}

export const hasFeatureHighlightPendingVisit = (
  storageKey: string,
): boolean => {
  try {
    return window.sessionStorage.getItem(pendingVisitKey(storageKey)) === 'true'
  } catch {
    return false
  }
}

/** Permanent dismiss: never show again for this identity in this browser. */
export const markFeatureHighlightSeen = (storageKey: string): void => {
  try {
    window.localStorage.setItem(storageKey, 'true')
    window.sessionStorage.removeItem(pendingVisitKey(storageKey))
  } catch {
    // Ignore storage failures; highlight may show again on next visit.
  }
}

/**
 * Session-only visit while eligibility is still loading.
 * Promoted to permanent seen once the user is confirmed eligible.
 */
export const markFeatureHighlightPendingVisit = (storageKey: string): void => {
  try {
    window.sessionStorage.setItem(pendingVisitKey(storageKey), 'true')
  } catch {
    // Ignore storage failures.
  }
}

export const shouldShowFeatureHighlight = (storageKey: string): boolean =>
  !hasSeenFeatureHighlight(storageKey)

const removeOrphanFeatureHighlightKeys = (storage: Storage): void => {
  const keysToRemove: string[] = []
  for (let index = 0; index < storage.length; index += 1) {
    const key = storage.key(index)
    if (!key) continue
    if (!key.includes(FEATURE_HIGHLIGHT_KEY_MARKER)) continue
    if (isActiveFeatureHighlightStorageKey(key)) continue
    keysToRemove.push(key)
  }
  keysToRemove.forEach(key => storage.removeItem(key))
}

/**
 * Removes stale FeatureHighlight keys from localStorage and sessionStorage.
 * Keeps keys for features still listed in FEATURE_HIGHLIGHTS.
 * Call when rotating the active highlight so old dismiss state does not linger.
 */
export const cleanupOrphanFeatureHighlightStorage = (): void => {
  try {
    if (typeof window === 'undefined') return
    removeOrphanFeatureHighlightKeys(window.localStorage)
    removeOrphanFeatureHighlightKeys(window.sessionStorage)
  } catch {
    // Ignore storage failures.
  }
}
